import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IPartnerFinSobRow } from '../../model'

import { getBaselineRows } from './getBaselineRows'
import { getRevenueMeasureField } from './getRevenueMeasureField'
import { getWeightedSob, TFinSobData } from './getWeightedSob'
import { prorateCurrentQuarterRows } from './prorateCurrentQuarterRows'
import { sumMeasure } from './sumMeasure'
import { IRevenueAndSobFigures, TCategoryRowScope } from './types'

const EMPTY_BASELINE: IRevenueAndSobFigures = {
  sales: null,
  sob: null,
}

interface IParams {
  rows: IPartnerFinSobRow[]
  /** The financial year the tile is showing. The baseline reads `fYear - 1`. */
  selectedFYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  currentFQuarter: number
  /** `null` outside YTD scope. Otherwise, the quarter matching `currentFQuarter` is prorated by
   *  this percentage instead of summed in full. */
  fQuarterActualsPercentage: number | null
  /** Defaults to `OVERALL_CATEGORY` (the offering total) when omitted. */
  category?: string
  categoryRowScope?: TCategoryRowScope
}

/** The tile's baseline column: what the partner did last year.
 *
 *  Revenue is additive, so it is a plain sum. SOB is a share, so it only rolls up as an average
 *  weighted by the same revenue measure — a big offering's share must count for more than a small
 *  one's. */
export const getRevenueAndSobBaseline = ({
  rows,
  selectedFYear,
  actualsType,
  isLocalCurrency,
  currentFQuarter,
  fQuarterActualsPercentage,
  category,
  categoryRowScope,
}: IParams): IRevenueAndSobFigures => {
  const isYtd = fQuarterActualsPercentage !== null
  const measureField = getRevenueMeasureField(actualsType, isLocalCurrency)

  if (measureField === null) {
    return EMPTY_BASELINE
  }

  const baselineRows = getBaselineRows({
    rows,
    selectedFYear,
    category,
    categoryRowScope,
  })

  const scopedRows = isYtd
    ? prorateCurrentQuarterRows({
        rows: baselineRows,
        measureField,
        currentFQuarter,
        fQuarterActualsPercentage,
      })
    : baselineRows

  const rowData = scopedRows.map<TFinSobData>((row) => ({
    sob: row.sob,
    revenue: row[measureField],
  }))

  const totalSales = sumMeasure(scopedRows, measureField)
  const weightedSob = getWeightedSob(rowData)

  return {
    sales: totalSales,
    sob: weightedSob,
  }
}
